import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '@/lib/api-client';
import { toast } from '@/utils/toast';
import {
  ManualIncomingResponseSchema,
  type ManualIncomingResponse,
} from '@/features/incoming/types';
import { INCOMING_KEY } from '@/features/incoming/useIncoming';
import type { NewConversionForm, NewIncomingForm, NewOutgoingForm } from './types';
import { toSellToBuyRate } from './useManualFxQuote';

export interface ManualIncomingBody {
  client_id: number;
  amount: string;
  currency_code: string;
  received_date: string;
  amount_sent?: string;
  fee?: string;
  description?: string;
  sender_name?: string;
  sender_account?: string;
  sender_swift?: string;
  sender_bank?: string;
  reference?: string;
}

/**
 * Form shape → endpoint shape.
 *
 * `amount` is what reached Montelux, not what the sender posted: it is the
 * figure the wallets move. The initial amount rides along for the record.
 *
 * A ticked "same client" box means there is no third-party sender to name,
 * so the field is left off entirely rather than sent as the client's own
 * name — the row should say nobody sent it, not invent a counterparty.
 * The account and bank it came from are kept either way: the client's own
 * external account is still somewhere the money left.
 *
 * `reference` is the wire's own reference, as the sender's bank carried it;
 * `description` is the operator's note. Neither stands in for the other.
 */
export function toManualIncomingBody(form: NewIncomingForm): ManualIncomingBody {
  const trimmed = (v: string | undefined) => {
    const out = (v ?? '').trim();
    return out.length > 0 ? out : undefined;
  };

  return {
    client_id: form.clientId,
    amount: form.amountReceived,
    currency_code: form.currencyCode,
    received_date: form.receivedDate,
    amount_sent: trimmed(form.amountSent),
    fee: trimmed(form.fee),
    description: trimmed(form.description),
    sender_name: form.sameClient ? undefined : trimmed(form.counterparty),
    sender_account: trimmed(form.senderAccount),
    sender_swift: trimmed(form.senderSwift),
    sender_bank: trimmed(form.senderBank),
    reference: trimmed(form.reference),
  };
}

export interface ManualOutgoingBody {
  client_id: number;
  idempotency_key: string;
  beneficiary_id: string;
  amount: string;
  currency_code: string;
  value_date: string;
  fee?: string;
  fee_currency_code?: string;
  reference?: string;
  description?: string;
  same_client: boolean;
}

/**
 * Form shape → endpoint shape for an outgoing wire (#272).
 *
 * `fee_currency_code` is left off when the operator kept "Same as amount":
 * the server then charges the fee in the wire's own currency, and sending
 * the two separately would be two ways of saying the same thing that could
 * drift apart.
 */
export function toManualOutgoingBody(
  form: NewOutgoingForm,
  idempotencyKey: string,
): ManualOutgoingBody {
  const trimmed = (v: string | undefined) => {
    const out = (v ?? '').trim();
    return out.length > 0 ? out : undefined;
  };

  return {
    client_id: form.clientId,
    idempotency_key: idempotencyKey,
    beneficiary_id: form.beneficiaryId ?? '',
    amount: form.amount,
    currency_code: form.currencyCode,
    value_date: form.valueDate,
    fee: trimmed(form.fee),
    fee_currency_code: trimmed(form.feeCurrencyCode),
    reference: trimmed(form.reference),
    description: trimmed(form.description),
    same_client: form.sameClient,
  };
}

export interface ManualOutgoingResponse {
  transaction_id: string;
}

/**
 * Ce que l'operateur lit quand une tentative echoue.
 *
 * Un 409 n'est pas une panne : le serveur dit que cette operation a deja
 * ete enregistree avec d'autres chiffres. L'argent a donc bouge, et
 * retaper ne reglera rien — il faut aller regarder ce qui est passe.
 */
export function bookingErrorMessage(err: ApiError): string {
  const detail = Array.isArray(err.message)
    ? err.message.join(' ')
    : typeof err.message === 'string'
      ? err.message.trim()
      : '';

  if (err.statusCode === 409) {
    // Ce qui diffère est dans la reponse du serveur — « amount 1000 ≠ 2000 ».
    // Le jeter laissait l'operateur deviner. Et le libelle reste neutre : un
    // 409 n'est pas toujours un rejeu, l'entrant en renvoie un sur un
    // conflit de statut.
    const lead = 'This operation conflicts with one already recorded.';
    return detail
      ? `${lead} ${detail}`
      : `${lead} Check what went through before entering it again.`;
  }

  return detail.length > 0 ? detail : 'Failed to book the operation';
}

export interface ManualConversionBody {
  client_id: number;
  idempotency_key: string;
  sell_amount: string;
  sell_currency_code: string;
  buy_currency_code: string;
  lp_rate: string;
  client_rate: string;
  margin_currency_code: string;
  value_date: string;
  description?: string;
}

/**
 * Form shape → endpoint shape for a conversion (#273).
 *
 * Neither the bought amount nor the margin travels: the server works both
 * out from the rates, and sending figures it is about to recompute would
 * invite the two to disagree. The rates are normalised to the direction
 * the server speaks before they go.
 */
export function toManualConversionBody(
  form: NewConversionForm,
  idempotencyKey: string,
): ManualConversionBody {
  const description = (form.description ?? '').trim();

  return {
    client_id: form.clientId,
    idempotency_key: idempotencyKey,
    sell_amount: form.sellAmount,
    sell_currency_code: form.sellCurrencyCode,
    buy_currency_code: form.buyCurrencyCode,
    lp_rate: toSellToBuyRate(form.lpRate, form.rateDirection) ?? form.lpRate,
    client_rate: toSellToBuyRate(form.clientRate, form.rateDirection) ?? form.clientRate,
    margin_currency_code: form.marginCurrencyCode,
    value_date: form.valueDate,
    description: description.length > 0 ? description : undefined,
  };
}

/** What the operator reads when the booking was sent but its receipt makes no sense. */
export const UNREADABLE_INCOMING_RECEIPT =
  'The server answered, but its reply could not be read. Check the Incoming Payments list before entering this wire again.';

export interface UseNewOperationResult {
  bookIncoming: (form: NewIncomingForm) => void;
  bookOutgoing: (form: NewOutgoingForm) => void;
  bookConversion: (form: NewConversionForm) => void;
  isBooking: boolean;
}

export function useNewOperation(onBooked?: () => void): UseNewOperationResult {
  const queryClient = useQueryClient();

  /**
   * La meme cle tant que l'operation n'a pas abouti.
   *
   * Le serveur reconnait un renvoi en comparant les `transaction_id` : avec
   * une cle neuve a chaque clic, un operateur qui reessaie apres un delai
   * d'attente — alors que le debit, lui, avait bien ete commis — payait une
   * seconde fois. Elle ne change qu'une fois l'operation passee.
   */
  /**
   * Une cle, une operation, jusqu'a ce qu'elle aboutisse.
   *
   * Elle ne tourne pas quand l'operateur modifie un champ. On l'avait fait,
   * et ca ouvrait pire que ce que ca fermait : apres un echec ou le debit
   * avait pourtant ete commis, corriger un montant — ou juste une
   * description — partait sous une cle neuve, et le client etait debite une
   * seconde fois.
   *
   * Le serveur, lui, sait quoi faire d'un renvoi qui ne decrit pas la meme
   * operation : il le refuse. Et il a raison de le refuser, parce qu'une
   * « correction » apres un debit commis n'en est pas une — l'argent est
   * parti. L'operateur doit aller voir, pas retaper.
   */
  /**
   * Une cle par nature d'operation.
   *
   * `payment` et `fx_trade` ont chacun leur unicite sur `transaction_id` :
   * la meme valeur peut donc exister des deux cotes. Une recherche de
   * transaction sans type regarde les paiements d'abord, si bien qu'un lien
   * de grand livre pose sur une conversion pouvait resoudre vers le virement
   * portant la meme cle.
   */
  const keys = useRef<Record<'outgoing' | 'conversion', string>>({
    outgoing: crypto.randomUUID(),
    conversion: crypto.randomUUID(),
  });
  const attemptKey = (kind: 'outgoing' | 'conversion') => keys.current[kind];

  const startNewOperation = (kind: 'outgoing' | 'conversion') => {
    keys.current[kind] = crypto.randomUUID();
  };

  /**
   * The create endpoint answers with a receipt, not a listing row: parsing it
   * as one rejected every successful booking for want of fields only the
   * list carries, and left the operator on a filled-in form with nothing to
   * stop a second click from booking the wire twice.
   */
  const incomingMutation = useMutation<ManualIncomingResponse, ApiError, NewIncomingForm>({
    mutationFn: async (form) => {
      const raw = await api.post<unknown>(
        '/api/convera/incoming/manual',
        toManualIncomingBody(form),
      );
      const receipt = ManualIncomingResponseSchema.safeParse(raw);
      if (!receipt.success) {
        // The request went through but its answer cannot be read, so whether
        // the wire was booked is unknown. Say that rather than "failed", and
        // keep the parser's diagnostics out of the operator's way.
        const error: ApiError = {
          statusCode: 0,
          message: UNREADABLE_INCOMING_RECEIPT,
          error: 'InvalidResponse',
          timestamp: new Date().toISOString(),
          path: '/api/convera/incoming/manual',
        };
        throw error;
      }
      return receipt.data;
    },
    onSuccess: (data) => {
      toast.success(`Operation booked: ${data.transaction_id}`);
      void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
      onBooked?.();
    },
    onError: (err) => {
      toast.error(bookingErrorMessage(err));
    },
  });

  const outgoingMutation = useMutation<ManualOutgoingResponse, ApiError, NewOutgoingForm>({
    mutationFn: async (form) =>
      api.post<ManualOutgoingResponse>(
        '/api/convera/payments/manual',
        toManualOutgoingBody(form, attemptKey('outgoing')),
      ),
    onSuccess: (data) => {
      toast.success(`Operation booked: ${data.transaction_id}`);
      startNewOperation('outgoing');
      void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
      onBooked?.();
    },
    onError: (err) => {
      toast.error(bookingErrorMessage(err));
    },
  });

  const conversionMutation = useMutation<ManualOutgoingResponse, ApiError, NewConversionForm>({
    mutationFn: async (form) =>
      api.post<ManualOutgoingResponse>(
        '/api/convera/fx/manual',
        toManualConversionBody(form, attemptKey('conversion')),
      ),
    onSuccess: (data) => {
      toast.success(`Operation booked: ${data.transaction_id}`);
      startNewOperation('conversion');
      void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
      onBooked?.();
    },
    onError: (err) => {
      toast.error(bookingErrorMessage(err));
    },
  });

  return {
    bookIncoming: (form) => incomingMutation.mutate(form),
    bookOutgoing: (form) => outgoingMutation.mutate(form),
    bookConversion: (form) => conversionMutation.mutate(form),
    isBooking:
      incomingMutation.isPending || outgoingMutation.isPending || conversionMutation.isPending,
  };
}
